import { guidedTourTarget } from '@/core/components/guided-tour'

import type { GuidedTourStep } from '@/core/components/guided-tour'
import type { EditorKind } from '@/features/studio-renew/steps'

const at = (name: string) => `[data-studio-tour="${name}"]`
const MATERIAL: Record<EditorKind, string> = {
  work: '소개 → 캐릭터 → 프롬프트 → 시작 장면 → 플러그인 → 설정 순서로 작품을 만들어요. 프롬프트와 플러그인은 건너뛸 수 있고, 각 단계에서 필요한 설정을 더할 수 있어요.',
  character:
    '캐릭터의 프로필·성격·말투를 만드는 곳이에요. 만든 캐릭터는 여러 작품에 출연시킬 수 있어요.',
  world:
    '여러 캐릭터가 공유할 배경과 장소를 만들어요. 특정 단어에 반응하는 키워드북도 세계관 안에서 관리해요.',
  command:
    '내 명령어의 실행 키와 결과 화면을 관리해요. 폰·에타·카톡·속마음·스냅은 별도의 벨루빗 명령어라 여기서 복제하지 않아요.',
  status:
    '호감도·장소처럼 대화 중 바뀌는 항목과 표시 화면을 만들어요. 완성한 상태창은 작품에 연결해 사용해요.',
}

export function homeTutorial(kind: EditorKind): GuidedTourStep[] {
  return [
    {
      target: at('tabs'),
      title:
        kind === 'work'
          ? '순서대로 첫 작품을 만들어요'
          : '만든 재료를 관리해요',
      description: MATERIAL[kind],
    },
    {
      target: at('create'),
      title: '빈 편집 화면에서 시작해요',
      description:
        '목록 첫 칸을 누르면 지금 보고 있는 종류의 제작을 시작해요. 작품은 실제로 내용을 입력하기 전까지 서버에 생성되지 않아요.',
    },
    {
      target: at('import'),
      title: '이미 쓴 설정은 이식하기로',
      description:
        'TXT 파일이나 텍스트를 가져와 항목별로 나눠요. 작품·캐릭터·세계관·상태창·명령어를 함께 확인하고, 가져올 내용과 적용 대상을 직접 고를 수 있어요. AI 정리와 기존 재료 비교도 사용할 수 있어요.',
    },
    {
      target: at('search'),
      title: '저장한 내용을 빠르게 찾아요',
      description:
        '이름과 소개로 지금 탭의 항목을 검색해요. 다른 종류의 재료를 찾으려면 위쪽 탭을 바꾸면 돼요.',
    },
    {
      target: at('list-tools'),
      title: '보기 편한 순서로 정리해요',
      description:
        '최근 수정 순이나 이름순으로 정렬할 수 있어요. 이미지가 있는 목록은 카드·리스트 보기도 바꿀 수 있어요.',
    },
    {
      target: at('first-item'),
      title: '이어서 만들고 관리해요',
      description:
        '항목을 누르면 편집을 이어가요. 더보기에서는 복제·삭제를 할 수 있어요. 편집 화면의 튜토리얼에서는 각 입력 항목을 자세히 안내해요.',
    },
    {
      target: at('empty'),
      title: '첫 항목을 만들어 볼까요?',
      description:
        '목록 첫 칸에서 직접 작성하거나 이식하기로 기존 설정을 가져오세요. 이 튜토리얼은 실제 내용을 만들거나 변경하지 않아요.',
    },
  ]
}

const FIELD_HELP: Record<string, string> = {
  이름: '캐릭터나 재료를 알아볼 이름이에요. 나중에 바꿀 수 있어요. 별표는 필수 항목이에요.',
  '한 줄 소개':
    '목록과 프로필에서 먼저 읽는 짧은 소개예요. 인물의 특징이나 이야기의 매력을 한 문장으로 적어 주세요.',
  '캐릭터 설정':
    '성격·배경·관계·행동 원칙처럼 AI가 이 인물을 연기할 때 알아야 할 내용을 적어요. 공개 소개와 구분해 작성해 주세요.',
  말버릇:
    '이 인물의 말투와 어휘를 보여 주는 짧은 샘플이에요. 예시에 등장하는 사건을 실제 과거로 설정하는 용도는 아니에요.',
  '거절할 요청':
    '이 캐릭터가 받아들이지 않을 행동이나 요청을 적어요. 인물별 지침이므로 다른 캐릭터의 규칙과 구분해 주세요.',
  '이용자 층':
    '작품 내용에 맞는 연령 범위를 선택해요. 서버에서 잠긴 작품은 이 값을 바꿀 수 없어요.',
  '공개 범위':
    '전체 공개·링크 공개·비공개 중 원하는 범위를 선택해요. 저장만으로 홈에 공개되지는 않으며 홈 노출에는 내부 확인이 필요해요.',
  키워드북:
    '대화에 특정 단어가 나올 때 참고할 세계관 정보를 적어요. 발동 키워드와 설명을 함께 채워 주세요.',
  '명령어 키':
    '대화에서 입력할 !로 시작하는 실행 키예요. 다섯 벨루빗 명령어와 겹치지 않는 키를 사용해 주세요.',
  '키 · 화면 이름':
    '실행할 !명령어와 이용자에게 보일 화면 이름을 정해요. 폰·에타·카톡·속마음·스냅은 벨루빗 명령어이므로 다른 키를 사용해 주세요.',
  '결과 화면 HTML':
    '명령어 결과를 어떻게 보여 줄지 정하는 선택 항목이에요. 아래 안내된 치환 문법을 사용하며, 비워 두면 기본 화면을 사용해요.',
  '세계관 본문':
    '이 배경의 규칙·역사·세력·분위기를 적어요. 특정 단어에만 필요한 짧은 정보는 키워드북으로 나눠 작성할 수 있어요.',
  장소: '장소 이름과 짧은 설명을 추가해요. 작품의 시작 장소와 컬렉션을 구성할 때 고를 수 있어요.',
  항목: '항목 이름·종류·시작값과 변화 규칙을 정해요. 숫자는 범위를, 선택형은 선택지를 함께 설정해 주세요. 숨긴 항목은 화면에 표시하지 않고 조건 판단에 사용해요.',
  미리보기:
    '저장한 항목의 시작값으로 화면을 미리 보여 줘요. 실제 대화에서 적용되는 값과 변화는 작품에 연결한 뒤 확인해 주세요.',
  '작품 이름':
    '이 이야기를 구분할 이름이에요. 캐릭터가 여럿인 작품에는 작품 이름이 필요해요.',
  주인공:
    '출연진 중 이야기의 중심이 될 캐릭터를 선택해요. 먼저 작품에 사용할 캐릭터를 연결해 주세요.',
  세계관:
    '이 작품의 공통 배경으로 쓸 세계관을 연결해요. 재료 원본은 세계관 탭에서 관리할 수 있어요.',
  '한 턴 최대 답하는 캐릭터':
    '한 번의 대화에서 답변에 참여할 캐릭터 수의 상한을 정해요. 전체 출연진 수와는 다른 설정이에요.',
  '상세 설명':
    '이용자가 작품 상세에서 읽는 공개 소개예요. 비공개 설정이나 AI에게만 전달할 지침과 구분해 작성해 주세요.',
  '탐색 표지 GIF':
    '탐색에서 사용할 움직이는 표지예요. GIF를 등록하지 않으면 기본 이미지를 사용해요.',
  타겟: '작품이 주로 향하는 이용자 취향을 선택해요. 연령에 관한 이용자 층 설정과는 별개예요.',
  '작품 삭제 후 처리':
    '작품을 내린 뒤에도 기존 이용자의 대화와 수집 기록을 어떻게 다룰지 안내하는 동의 항목이에요. 내용을 읽고 선택해 주세요.',
}

const STEP_HELP: Record<string, string> = {
  profile:
    '이름·대표 이미지·한 줄 소개를 작성해요. 채팅용 프로필 사진은 선택 항목이에요.',
  persona:
    '성격·배경·관계·행동 원칙을 적어요. AI가 캐릭터를 연기할 때 참고하는 설정이에요.',
  habits:
    '말투 샘플과 캐릭터가 거절할 요청을 정해요. 샘플 속 사건과 실제 기억은 구분해 주세요.',
  scenes:
    '상황이나 표정에 맞는 캐릭터 이미지를 추가해요. 작품 컬렉션과는 별도로 관리해요.',
  world:
    '이 캐릭터가 사용할 세계관을 연결하거나 간단한 배경을 직접 적을 수 있어요.',
  cast: '캐릭터의 이름과 모습을 정한 뒤 성격과 말투를 작성해요. 이미 만든 캐릭터로 시작하거나 필요한 경우 최대 6명까지 선택할 수 있어요.',
  starts:
    '이용자가 처음 만날 장면을 만들어요. 시작마다 제목과 캐릭터의 첫 대사가 필요하며 여러 시작을 준비할 수도 있어요.',
  intro:
    '작품 이름·소개·표지·태그를 정해요. 이용자가 작품에서 가장 먼저 보는 정보예요.',
  publish:
    '이용 연령과 공개 범위, 댓글 여부를 정해요. 저장과 실제 홈 노출은 별개예요.',
  tools:
    '호감도나 장소를 보여 주는 상태창, 속마음이나 휴대폰 보기 같은 명령어를 골라요. 기본 대화만 원하면 건너뛰어도 돼요.',
  status:
    '이 작품에서 보여 줄 상태창을 연결해요. 항목과 변화 규칙은 연결한 상태창 원본에서 관리해요.',
  album:
    '현재 상황과 내레이터를 정하고 컬렉션의 장소·장면을 구성해요. 캐릭터의 말투 설정과 구분해 주세요.',
  commands:
    '폰·에타·카톡·속마음과 내 명령어를 작품에 연결해요. 현재 장면의 이미지를 만드는 스냅은 모든 작품에 기본으로 들어 있어요.',
  basic:
    '세계관 이름과 한 줄 소개를 적어요. 같은 배경을 여러 캐릭터에게 연결할 수 있어요.',
  body: '세계관의 배경과 규칙, 장소별 설명을 작성해요. 캐릭터 개인의 설정과 나누면 관리하기 편해요.',
  keywords:
    '특정 단어가 나올 때 참고할 정보를 키워드와 묶어 적어요. 키워드북은 이 세계관에 함께 저장돼요.',
  visibility:
    '세계관의 공개 범위를 선택해요. 작품의 공개 범위는 작품에서 별도로 관리해요.',
  edit: '이 재료의 이름과 동작을 정하고 미리보기로 결과를 확인해요. 만든 뒤 작품에서 연결하면 사용할 수 있어요.',
}

/**
 * 화면 틀 설명(단계 줄 · ⋯ · 저장)은 처음 한 번만 — 모든 편집 화면에서 같은 자리 · 같은 모양이라 한 번 보면 끝이다.
 * 두 번째부터는 지금 단계 · 칸 안내만 나온다(이 브라우저 기준). 대상이 없는 화면(단계 줄 없는 상태창 등)에서는 본 것으로 치지 않는다
 */
const FRAME_SEEN = 'veluvit:studio-tour:frame-seen'
function firstTimeOnly(name: string, step: GuidedTourStep): GuidedTourStep[] {
  if (!guidedTourTarget(step.target)) return []
  try {
    const seen = (localStorage.getItem(FRAME_SEEN) ?? '').split(',')
    if (seen.includes(name)) return []
    localStorage.setItem(FRAME_SEEN, [...seen.filter(Boolean), name].join(','))
  } catch {
    // 저장소를 못 쓰는 창(사생활 보호 등)은 매번 보여 준다
  }
  return [step]
}

export function editorTutorial(
  kind: EditorKind,
  step: string,
  root: HTMLElement | null
): GuidedTourStep[] {
  const fields = Array.from(
    root?.querySelectorAll<HTMLElement>('[data-studio-tour-label]') ?? []
  ).map((element): GuidedTourStep => {
    const label = element.dataset.studioTourLabel ?? '입력 항목'
    const hint = element.querySelector('p')?.textContent
    const baseLabel = label.replace(/\s+\d+\/\d+$/, '')
    return {
      target: () => element,
      title: label,
      description:
        FIELD_HELP[baseLabel] ??
        hint ??
        '이 항목의 안내와 입력 한도를 확인해 주세요. 수정한 내용은 자동으로 저장돼요.',
    }
  })
  return [
    ...firstTimeOnly('steps', {
      target: at('steps'),
      title: '필요한 단계부터 편집해요',
      description:
        '단계를 눌러 언제든 오갈 수 있어요. 별표는 필수 단계, 초록 체크는 채운 단계, 점선은 선택 단계예요. 오른쪽 미리보기로 입력 결과를 보고, 테스트 버튼으로 직접 대화할 수 있어요.',
    }),
    {
      target: at('editor-body'),
      title: '지금 단계에서 할 일',
      description: STEP_HELP[step] ?? MATERIAL[kind],
    },
    ...fields,
    {
      target: at('test-body'),
      title: '직접 대화하며 확인해요',
      description:
        '입력창 옆 모드 버튼에서 모델과 대화 방식을 고를 수 있어요. 변경한 캐릭터나 시작 설정으로 확인하려면 새 테스트를 시작해 주세요.',
    },
    ...firstTimeOnly('more', {
      target: at('more'),
      title: '미리보기와 링크 복사',
      description:
        '더보기에서 미리보기를 크게 열거나 공개 작품의 링크를 복사할 수 있어요. 이식하기와 새로 만들기는 위쪽의 이름 버튼을 사용해요.',
    }),
    ...firstTimeOnly(kind === 'work' ? 'save:work' : 'save', {
      target: at('save'),
      title: '마지막으로 저장을 확인해요',
      description:
        kind === 'work'
          ? '작품은 미완성이어도 저장하고 나중에 이어 쓸 수 있어요. 완성도에서 남은 필수 항목과 선택 항목을 확인해 주세요. 공개와 홈 노출 여부는 서버에서 확인해요.'
          : '수정한 내용은 자동 저장돼요. 저장 버튼은 현재 화면을 유지하며 저장 상태를 확인해요. 아직 다 쓰지 않았어도 저장해 둘 수 있어요.',
    }),
  ]
}
